import { getEnv } from "@/config/env";
import { getJson } from "@/api/http";
import {
  postWithProblemDetails,
  type FlowError,
  type FlowResult,
} from "@/api/problemDetails";
import type { Result } from "@/types/layout";
import type {
  CartCurrencyStatus,
  CurrencyBannerStatus,
  CurrencyConflictDetails,
  CurrencyMigrationExecuteResult,
  CurrencyMigrationPreview,
} from "@/types/currencyMigration";

const PREVIEW_ENDPOINT = "/member/currency-migration/preview";
const EXECUTE_ENDPOINT = "/member/currency-migration/execute";

/**
 * 409 `errorCode` values the execute can answer with, so the FE reroutes instead
 * of showing a generic error:
 *  - `preview_changed`     → their subscriptions moved since the dialog was drawn
 *    (planHash mismatch) — refetch and let them confirm the real figures.
 *  - `already_migrated`    → this exact switch already ran (another tab) — treat as
 *    done and retry the checkout.
 *  - `already_in_progress` → another execute is running — refetch.
 *  - `nothing_to_migrate`  → they no longer conflict; the checkout will now work,
 *    so just retry it.
 *  - `blocked`             → something must not be moved automatically — show the
 *    blocked state naming it.
 *  - `feature_disabled`    → kill switch is on; the member falls back to paying in
 *    their existing currency or contacting support.
 */
const ERROR_CODES = [
  "preview_changed",
  "already_migrated",
  "already_in_progress",
  "nothing_to_migrate",
  "blocked",
  "feature_disabled",
] as const;

export type CurrencyMigrationErrorKind = (typeof ERROR_CODES)[number];

export type CurrencyMigrationError = FlowError<CurrencyMigrationErrorKind>;

export type CurrencyMigrationResult<T> = FlowResult<
  T,
  CurrencyMigrationErrorKind
>;

/**
 * What switching to the cart's currency would do. Always 200 — a member with
 * nothing to switch comes back `eligible: false` with a reason. The target
 * currency is read from their cart on the backend, so there is nothing to pass:
 * letting the client name it would allow a member to be migrated into a currency
 * they are not actually buying in.
 */
export async function fetchCurrencyMigrationPreview(
  signal?: AbortSignal
): Promise<Result<CurrencyMigrationPreview>> {
  const env = getEnv();
  return getJson<CurrencyMigrationPreview>(
    `${env.apiBaseUrl}${PREVIEW_ENDPOINT}`,
    { signal }
  );
}

/**
 * Moves the member's billing to the cart's currency: each live subscription is
 * recreated on a new Stripe customer, trialing until the date it would have
 * renewed, and the originals are cancelled. Nothing is charged.
 *
 * `planHash` must be the one from the rendered preview — the backend 409s with
 * `preview_changed` otherwise, so a stale dialog can never migrate something the
 * member was not shown.
 */
export async function executeCurrencyMigration(
  planHash: string
): Promise<CurrencyMigrationResult<CurrencyMigrationExecuteResult>> {
  const env = getEnv();
  return postWithProblemDetails<
    CurrencyMigrationExecuteResult,
    CurrencyMigrationErrorKind
  >(`${env.apiBaseUrl}${EXECUTE_ENDPOINT}`, { planHash }, ERROR_CODES);
}

/**
 * Reads the two currencies off a `currency_conflict` ProblemDetails body. Returns
 * null when the shape isn't what we expect, so a malformed body degrades to the
 * generic checkout error rather than opening a dialog with blank currencies.
 */
export function readCurrencyConflict(
  details: Record<string, unknown> | undefined
): CurrencyConflictDetails | null {
  const existing = details?.existingCurrency;
  const requested = details?.requestedCurrency;
  if (typeof existing !== "string" || typeof requested !== "string")
    return null;
  if (!existing || !requested) return null;
  return { existingCurrency: existing, requestedCurrency: requested };
}

const CART_STATUS_ENDPOINT = "/cart/currency-status";

/**
 * Whether the member can pay for the cart they are looking at, currency-wise.
 * Always 200 — guests and unknowns come back `hasConflict: false` — so the cart can
 * call this unconditionally without special-casing anonymous visitors.
 */
export async function fetchCartCurrencyStatus(
  signal?: AbortSignal
): Promise<Result<CartCurrencyStatus>> {
  const env = getEnv();
  return getJson<CartCurrencyStatus>(
    `${env.apiBaseUrl}${CART_STATUS_ENDPOINT}`,
    { signal }
  );
}

const BANNER_STATUS_ENDPOINT = "/member/currency-migration/status";

/**
 * Whether my-account should offer the currency switch. Cheap enough to call on
 * every dashboard render — unlike the preview, which resolves (and can create) a
 * Stripe customer and so must only run once the member opens the switch.
 *
 * Always 200, and silent when unsure: every unknown comes back
 * `shouldPrompt: false`.
 */
export async function fetchCurrencyBannerStatus(
  signal?: AbortSignal
): Promise<Result<CurrencyBannerStatus>> {
  const env = getEnv();
  return getJson<CurrencyBannerStatus>(
    `${env.apiBaseUrl}${BANNER_STATUS_ENDPOINT}`,
    { signal }
  );
}
